import React from 'react';
import { Grid, IconButton, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import useTheme from '@mui/material/styles/useTheme';
import { observer } from 'mobx-react';
import { SurveyStore } from '@gamiphy/loyalty-station-desktop/store/survey';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { DisplayStringTrans, Trans } from '@gamiphy/react-i18n';
import { AppSurveyDto } from '@coretava/api/events-service';
import { QuestionFactory } from './questions';
import SurveyButton from './SurveyButton';
import { useSurveyForm } from '../hook';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#f4f4f5',
    width: '100%',
    height: '100%',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '0px',
      background: 'transparent',
    },
    '&::-webkit-scrollbar-track': {
      margin: theme.spacing(1),
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'transparent',
      borderRadius: '50px',
    },
  },
  header: {
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.primary.main,
    fontSize: '1.4rem',
    fontWeight: 500,
    padding: theme.spacing(2),
  },
  sectionWrapper: {
    display: 'flex',
    flex: 2,
    padding: theme.spacing(2),
  },
  questionsWrapper: {
    display: 'flex',
    flex: 2,
    width: '100%',
  },
  questionWrapper: {
    display: 'flex',
    width: '100%',
  },
  title: {
    textAlign: 'left',
    color: '#000',
    fontWeight: 500,
    fontSize: '1.4rem',
    marginLeft: theme.spacing(1),
  },
}));

type Props = {
  onDone: () => void;
  appSurvey?: AppSurveyDto
}

const SurveyContent: React.FC<Props> = observer(({ onDone, appSurvey }) => {
  if (!appSurvey) {
    return null;
  }
  const theme = useTheme();
  const classes = useStyles();
  const {
    onSubmit, data, isFirst, handleBack, control, formState, shouldSubmit, dataIndex,
  } = useSurveyForm({ groupBy: 'section', appSurvey });

  const ArrowIcon = React.useMemo(() => {
    if (theme.direction === 'rtl') {
      return <ArrowForward fontSize="large" />;
    }
    return <ArrowBack fontSize="large" />;
  }, [theme.direction]);

  return (
    <form style={{ width: '100%', height: '100%' }} onSubmit={onSubmit(onDone)}>
      <Grid container direction="column" wrap="nowrap" className={classes.root}>
        <Grid item className={classes.sectionWrapper}>
          <Grid container direction="column" wrap="nowrap" spacing={1}>
            <Grid item>
              <Grid container alignItems="center">
                {
                  !isFirst && (
                    <IconButton onClick={handleBack} size="large">
                      {ArrowIcon}
                    </IconButton>
                  )
                }
                <Typography className={classes.title} noWrap>
                  {data.title ? <DisplayStringTrans displayString={data.title} />
                    : (
                      <Trans
                        i18nKey="survey.report.list.question"
                        defaults="Question {{number}}"
                        values={{ number: dataIndex + 1 }}
                      />
                    )}
                </Typography>
              </Grid>
            </Grid>
            <Grid className={classes.questionsWrapper} item>
              <Grid container direction="column" spacing={2}>
                {data.questions.map((question) => (
                  <Grid item key={question.id} className={classes.questionWrapper}>
                    <QuestionFactory control={control} question={question} />
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid item>
              <SurveyButton isLoading={SurveyStore.loading || formState.isSubmitting} isDisabled={!formState.isValid} isSubmit={shouldSubmit} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
});

export default SurveyContent;
